import React from 'react';
import {
  BarChart3,
  Bot,
  GitBranch,
  MessageCircle,
  Mic,
  Sparkles,
  Workflow
} from 'lucide-react';

export function AutomationHeroMockups({ mock }) {
  return (
    <div className="relative mx-auto w-full max-w-xl lg:max-w-none lg:mx-0 min-h-[400px] sm:min-h-[460px] lg:min-h-[520px] overflow-x-hidden">
      <div
        className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-violet-500/[0.08] via-transparent to-cyan-500/[0.07] blur-2xl pointer-events-none animate-pulse"
        style={{ animationDuration: '4s' }}
        aria-hidden
      />

      {/* Chat IA */}
      <div className="absolute left-0 top-6 sm:top-10 w-[min(100%,270px)] rounded-2xl border border-white/[0.12] bg-slate-950/85 backdrop-blur-xl shadow-[0_24px_80px_-24px_rgba(139,92,246,0.3)] p-4 z-20 rotate-[-2deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2 mb-3">
          <Bot className="w-4 h-4 text-violet-400" aria-hidden />
          <span className="text-[10px] font-semibold uppercase tracking-wider text-white/45">{mock.chatLabel}</span>
        </div>
        <div className="space-y-2">
          <div className="rounded-xl rounded-tl-sm bg-violet-500/20 border border-violet-500/25 px-3 py-2 max-w-[85%]">
            <div className="h-1.5 w-24 rounded-full bg-white/20 mb-1" />
            <div className="h-1.5 w-16 rounded-full bg-white/10" />
          </div>
          <div className="rounded-xl rounded-tr-sm bg-white/[0.06] border border-white/[0.08] px-3 py-2 max-w-[75%] ml-auto">
            <div className="h-1.5 w-20 rounded-full bg-white/15" />
          </div>
          <div className="rounded-xl rounded-tl-sm bg-violet-500/15 border border-violet-500/20 px-3 py-2 max-w-[80%]">
            <div className="h-1.5 w-28 rounded-full bg-white/15" />
          </div>
        </div>
      </div>

      {/* WhatsApp automation */}
      <div className="absolute right-0 top-0 w-[min(100%,240px)] rounded-2xl border border-white/[0.12] bg-slate-950/80 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(52,211,153,0.25)] p-3 z-10 rotate-[2deg] sm:right-2 lg:right-6 hover:rotate-0 transition-transform duration-500">
        <div className="rounded-xl border border-white/[0.08] bg-gradient-to-br from-emerald-500/10 to-slate-900/80 p-3">
          <div className="flex items-center gap-2 mb-2">
            <MessageCircle className="w-4 h-4 text-emerald-400" aria-hidden />
            <span className="text-[10px] font-semibold text-white/70">{mock.whatsappLabel}</span>
          </div>
          <div className="space-y-1.5">
            <div className="h-5 rounded-lg bg-emerald-500/15 border border-emerald-500/20 px-2 flex items-center">
              <div className="h-1 w-full rounded-full bg-white/12" />
            </div>
            <div className="h-5 rounded-lg bg-white/[0.04] border border-white/[0.05]" />
          </div>
        </div>
      </div>

      {/* Voice IA */}
      <div className="absolute left-10 sm:left-16 bottom-32 sm:bottom-36 w-[140px] rounded-xl border border-cyan-500/25 bg-cyan-950/50 backdrop-blur-md px-3 py-2.5 z-30 shadow-lg rotate-[3deg] hover:rotate-0 transition-transform duration-500">
        <div className="flex items-center gap-2">
          <Mic className="w-4 h-4 text-cyan-400 shrink-0" aria-hidden />
          <div>
            <p className="text-[9px] font-bold text-cyan-200/95">{mock.voiceLabel}</p>
            <div className="flex gap-0.5 mt-1">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="w-0.5 rounded-full bg-cyan-400/60" style={{ height: `${8 + i * 3}px` }} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard analytics */}
      <div className="absolute right-6 sm:right-12 bottom-24 sm:bottom-28 w-[min(100%,200px)] rounded-xl border border-indigo-500/25 bg-indigo-950/40 backdrop-blur-md px-3 py-2.5 z-40 shadow-lg">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-indigo-400" aria-hidden />
          <span className="text-[10px] font-bold text-indigo-200/95">{mock.analyticsLabel}</span>
        </div>
        <div className="grid grid-cols-3 gap-1">
          {[45, 62, 38].map((h, i) => (
            <div key={i} className="rounded bg-indigo-500/20 border border-indigo-500/15 flex items-end p-0.5" style={{ height: `${h}px` }}>
              <div className="w-full h-1 rounded-full bg-indigo-400/40" />
            </div>
          ))}
        </div>
      </div>

      {/* Fluxo automatizado */}
      <div className="absolute left-1/2 -translate-x-1/2 bottom-4 sm:bottom-8 w-[min(100%,290px)] rounded-2xl border border-violet-500/20 bg-gradient-to-br from-slate-950/95 to-slate-900/85 backdrop-blur-xl shadow-[0_28px_90px_-28px_rgba(139,92,246,0.25)] p-4 z-25">
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-200/80">{mock.flowLabel}</span>
          <Workflow className="w-3.5 h-3.5 text-violet-400" aria-hidden />
        </div>
        <div className="flex items-center justify-between gap-1">
          {[GitBranch, Sparkles, Bot].map((Icon, i) => (
            <React.Fragment key={i}>
              <div className="w-8 h-8 rounded-lg bg-violet-500/20 border border-violet-500/25 flex items-center justify-center shrink-0">
                <Icon className="w-3.5 h-3.5 text-violet-300" aria-hidden />
              </div>
              {i < 2 && <div className="flex-1 h-px bg-violet-500/30" />}
            </React.Fragment>
          ))}
        </div>
        <p className="text-[9px] text-violet-300/70 mt-3 text-center flex items-center justify-center gap-1">
          <Sparkles className="w-3 h-3" aria-hidden />
          {mock.live}
        </p>
      </div>
    </div>
  );
}
